import { useCurrentStateAndParams } from '@uirouter/react';
import { Code, FileText } from 'lucide-react';

import { PageHeader } from '@@/PageHeader';
import { Widget } from '@@/Widget/Widget';
import { WidgetBody } from '@@/Widget';
import { WidgetTabs, Tab, useCurrentTabIndex } from '@@/Widget/WidgetTabs';
import { Alert } from '@@/Alert';
import { InlineLoader } from '@@/InlineLoader';
import { CodeEditor } from '@@/CodeEditor';

import { YAMLInspector } from '../../components/YAMLInspector';
import { useDescribeResource } from '../../describe/useDescribeResource';

import { useCustomResourceDefinition } from './queries/useCustomResourceDefinition';

export function CustomResourceDefinitionDetailsView() {
  const {
    params: { endpointId: environmentId, name },
  } = useCurrentStateAndParams();

  const {
    data: yamlData,
    isLoading: isYamlLoading,
    isError: isYamlError,
  } = useCustomResourceDefinition(environmentId, name, { yaml: true });

  const tabs: Tab[] = [
    {
      name: 'YAML',
      icon: Code,
      widget: (
        <div className="row">
          <div className="col-sm-12">
            <Widget>
              <WidgetBody>
                <YAMLInspector
                  identifier="crd-yaml"
                  data={yamlData?.toString() || ''}
                  authorised
                  system={false}
                  hideMessage
                  isLoading={isYamlLoading}
                  isError={isYamlError}
                  data-cy="crd-yaml"
                />
              </WidgetBody>
            </Widget>
          </div>
        </div>
      ),
      selectedTabParam: 'yaml',
    },
    {
      name: 'Describe',
      icon: FileText,
      widget: (
        <DescribeTabContent
          name={name}
          resourceType="customresourcedefinition"
        />
      ),
      selectedTabParam: 'describe',
    },
  ];

  const currentTabIndex = useCurrentTabIndex(tabs);

  if (!yamlData) {
    return null;
  }

  return (
    <>
      <PageHeader
        title="Custom Resource Definition details"
        breadcrumbs={[
          {
            label: 'Custom Resource Definitions',
            link: 'kubernetes.moreResources.customResources',
            linkParams: { tab: 'definitions' },
          },
          name,
        ]}
        reload
      />
      <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />
      {tabs[currentTabIndex].widget}
    </>
  );
}

function DescribeTabContent({
  name,
  resourceType,
}: {
  name: string;
  resourceType: string;
}) {
  const { data, isLoading, isError } = useDescribeResource(name, resourceType);
  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget>
          <WidgetBody>
            {isLoading && <InlineLoader>Loading...</InlineLoader>}
            {isError && (
              <Alert color="error" title="Error">
                Error loading resource details
              </Alert>
            )}
            {!isLoading && !isError && (
              <CodeEditor
                id="describe-resource"
                data-cy="describe-resource"
                readonly
                value={data?.describe ?? ''}
                type="yaml"
              />
            )}
          </WidgetBody>
        </Widget>
      </div>
    </div>
  );
}
